import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money, ordinal, canonRodeoEvent } from "@/lib/ui";
import usePersistentState from "@/lib/usePersistentState";
import useIsMobile from "@/lib/useIsMobile";
import useFollowing from "@/lib/useFollowing";
import { useAuth } from "@/context/AuthContext";
import { Users, Search, Star, Loader2, ChevronDown, MapPin, Trophy, CalendarClock, X, Crown, Flame } from "lucide-react";
import { CowIcon } from "@/components/icons/CowIcon";
import RiderMedia from "@/components/RiderMedia";

const WON = "#16A34A";

export default function MyRiders() {
  const m = useIsMobile();
  const nav = useNavigate();
  const { user } = useAuth();
  const { following, isFollowing, follow, unfollow } = useFollowing();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [justFollowed, setJustFollowed] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selfName, setSelfName] = useState(null);
  const [totals, setTotals] = useState({});
  const [trending, setTrending] = useState(new Set());
  const [evFilter, setEvFilter] = usePersistentState("rp_myriders_event", "");

  useEffect(() => {
    api.get("/me/competitor").then((r) => setSelfName((r.data?.competitor_name || "").trim() || null)).catch(() => {});
    api.get("/rider-media/most-watched").then((r) => setTrending(new Set((r.data?.riders || []).map((x) => x.rider.trim().toLowerCase())))).catch(() => {});
  }, []);

  // Season + CFR money for every followed rider, shown on each (collapsed) row.
  useEffect(() => {
    if (!following.length) { setTotals({}); return; }
    api.get("/competitors/following-totals").then((r) => setTotals(r.data?.totals || {})).catch(() => {});
  }, [following.length]);

  const closeSearch = () => { setSearchOpen(false); setQ(""); };

  // Arriving from the Map with a rider selected → open that rider's card.
  useEffect(() => {
    const focus = (() => { try { return localStorage.getItem("rp-focus-rider"); } catch { return null; } })();
    if (focus) {
      setJustFollowed(focus);
      try { localStorage.removeItem("rp-focus-rider"); } catch {}
    }
  }, []);

  // Follow from search → close the overlay / clear the box and reveal the rider (expanded) in the list below.
  const handleFollow = (name) => { setJustFollowed(name); setSearchOpen(false); setQ(""); follow(name); };

  useEffect(() => {
    const qq = q.trim();
    if (qq.length < 2) { setResults(null); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get(`/competitors/search?q=${encodeURIComponent(qq)}`)
        .then((r) => setResults(r.data || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "min-h-screen bg-[#F4F6F8]";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-[#161B22] border border-white/10" : "bg-white border border-[#E4E7EB] shadow-sm";
  const inp = m ? "bg-white/5 border-white/10 text-slate-100 placeholder-slate-500" : "bg-white border-[#D7DBE0] text-[#14181F] placeholder-slate-400";
  const sub = m ? "text-slate-400" : "text-slate-500";

  const isPro = !!user?.is_pro;

  const resultRows = (
    searching && !results ? (
      <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>
    ) : results && results.length ? (
      results.map((r) => {
        const f = isFollowing(r.name);
        return (
          <div key={r.name} data-testid={`my-riders-result-${r.name}`} className={`flex items-center justify-between gap-3 px-4 py-2.5 ${m ? "border-b border-white/5" : "border-b border-[#EEF0F3]"} last:border-0`}>
            <div className="min-w-0">
              <div className={`text-[15px] font-semibold truncate ${title}`}>{r.name}</div>
              {r.hometown && <div className={`text-[12px] truncate ${sub}`}>{r.hometown}</div>}
            </div>
            <button onClick={() => (f ? unfollow(r.name) : handleFollow(r.name))} data-testid={`my-riders-follow-${r.name}`}
              className={`shrink-0 flex items-center gap-1.5 text-[13px] font-semibold px-3 py-1.5 rounded-full ${f ? (m ? "bg-white/10 text-slate-300" : "bg-[#EEF0F3] text-slate-600") : "bg-[#C8000F] text-white"}`}>
              <Star className={`w-3.5 h-3.5 ${f ? "fill-current" : ""}`} /> {f ? "Following" : "Follow"}
            </button>
          </div>
        );
      })
    ) : (
      <div className={`px-4 py-6 text-[13px] ${sub}`}>No contestants match “{q}”.</div>
    )
  );

  return (
    <div className={page} data-testid="my-riders-page">
      {/* Mobile full-screen search overlay — matches the rodeo search UX (no layout jump/flicker) */}
      {m && searchOpen && (
        <div className="fixed inset-0 z-[3000] bg-[#0C1017] flex flex-col" data-testid="my-riders-search-overlay">
          <div className="shrink-0 flex items-center gap-2 p-3 border-b border-white/10 bg-[#0F141B]" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
            <div className="relative flex-1 min-w-0">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a contestant by name…" data-testid="my-riders-search-input"
                className="w-full text-[16px] bg-white/5 border border-white/10 text-slate-100 placeholder-slate-500 rounded-lg pl-9 pr-3 py-2.5 focus:outline-none focus:border-[#C8000F]" />
            </div>
            <button onClick={closeSearch} data-testid="my-riders-search-cancel"
              className="shrink-0 px-3 py-2 text-[14px] font-semibold text-slate-300 active:text-white">Cancel</button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            {q.trim().length >= 2 ? resultRows : (
              <div className="px-4 py-6 text-[13px] text-slate-500">Start typing a contestant's name…</div>
            )}
          </div>
        </div>
      )}

      <div className={`px-4 sm:px-6 py-5 border-b flex items-center gap-2 ${m ? "border-white/10" : "border-[#E4E7EB] bg-white"}`}>
        <Users className="w-6 h-6 text-[#C8000F]" />
        <h1 className={`font-display text-2xl font-extrabold tracking-tight ${title}`}>My Riders</h1>
      </div>

      {false ? (
        <div className="p-4 sm:p-6 max-w-2xl">
          <div className={`rounded-2xl p-6 text-center ${card}`} data-testid="my-riders-gate">
            <Crown className="w-10 h-10 mx-auto text-[#E0A526]" />
            <h2 className={`font-display text-xl font-extrabold mt-3 ${title}`}>Follow your rivals</h2>
            <p className={`text-sm mt-2 ${sub}`}>Track any contestant — see every rodeo they've entered, where they're up next, and every placing, time and cheque of their season. Available on a Rodeo Pilot plan.</p>
            <button onClick={() => nav("/choose-plan")} data-testid="my-riders-upgrade-btn"
              className="mt-5 inline-flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white font-semibold px-5 py-2.5 rounded-lg">
              <Crown className="w-4 h-4" /> See plans
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 sm:p-6 max-w-3xl space-y-6">
          {/* Search */}
          <div>
            {m ? (
              <button type="button" onClick={() => setSearchOpen(true)} data-testid="my-riders-search-open"
                className={`w-full flex items-center gap-2 rounded-lg border pl-9 pr-3 py-2.5 text-left relative ${inp}`}>
                <Search className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${sub}`} />
                <span className="text-[16px] text-slate-500 truncate">Search a contestant by name…</span>
              </button>
            ) : (
              <>
                <div className="relative">
                  <Search className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${sub}`} />
                  <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="my-riders-search-input"
                    placeholder="Search a contestant by name…"
                    className={`w-full text-[16px] rounded-lg border pl-9 pr-9 py-2.5 focus:outline-none focus:border-[#C8000F] ${inp}`} />
                  {q && <button onClick={() => setQ("")} data-testid="my-riders-search-clear" className={`absolute right-3 top-1/2 -translate-y-1/2 ${sub}`}><X className="w-4 h-4" /></button>}
                </div>
                {q.trim().length >= 2 && (
                  <div className={`mt-2 rounded-lg overflow-hidden ${card}`} data-testid="my-riders-search-results">
                    {resultRows}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Event filter */}
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="my-riders-event-filter">
            {["", ...RIDER_EVENTS].map((e) => (
              <button key={e || "all"} onClick={() => setEvFilter(e)} data-testid={`my-riders-event-${(e || "all").replace(/\s/g, "-")}`}
                className={`shrink-0 text-[12px] font-semibold px-3 py-1.5 rounded-full border transition-colors ${evFilter === e ? "bg-[#C8000F] border-[#C8000F] text-white" : m ? "border-white/10 text-slate-300" : "border-[#D7DBE0] text-slate-600 bg-white hover:border-slate-400"}`}>
                {e || "All events"}
              </button>
            ))}
          </div>

          {/* Followed list */}
          <div>
            <h2 className={`font-display text-lg font-extrabold mb-2 ${title}`}>Following {following.length ? `(${following.length})` : ""}</h2>
            {!following.length ? (
              <div className={`rounded-xl p-6 text-center ${card}`} data-testid="my-riders-empty">
                <p className={`text-sm ${sub}`}>You're not following anyone yet. Search a contestant above and tap <b>Follow</b> to start tracking them.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {following.map((name) => {
                  const isSelf = selfName && name.toLowerCase() === selfName.toLowerCase();
                  return (
                    <RiderCard key={name} name={name} m={m} card={card} title={title} sub={sub} autoOpen={name === justFollowed} isSelf={isSelf} preTotal={totals[name]} isAdmin={user?.role === "admin"} trending={trending.has(name.trim().toLowerCase())} evFilter={evFilter}
                      onUnfollow={isSelf ? null : () => unfollow(name)} onMap={() => nav(`/map?rider=${encodeURIComponent(name)}`)} />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function RiderCard({ name, m, card, title, sub, onUnfollow, onMap, autoOpen, isSelf, preTotal, isAdmin, trending, evFilter }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(!!autoOpen);
  useEffect(() => { if (autoOpen) setOpen(true); }, [autoOpen]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [followers, setFollowers] = useState(null);

  useEffect(() => {
    if (open && !data) {
      setLoading(true);
      api.get(`/competitors/profile?name=${encodeURIComponent(name)}`)
        .then((r) => setData(r.data))
        .catch(() => setData({ upcoming: [], results: [], pins: [], counts: {} }))
        .finally(() => setLoading(false));
      api.get(`/rider-media/follower-count?name=${encodeURIComponent(name)}`).then((r) => setFollowers(r.data?.followers ?? null)).catch(() => {});
      api.post(`/rider-media/view`, { name }).catch(() => {});
    }
  }, [open, data, name]);

  const rowBorder = m ? "border-white/5" : "border-[#EEF0F3]";
  const chip = m ? "bg-white/5 text-slate-300" : "bg-[#F1F3F5] text-slate-600";

  // One unified rodeo list: show the result + winnings on a rodeo when they placed,
  // otherwise show the upcoming/entered draw line. (Previously these were two lists.)
  const rodeoRows = useMemo(() => {
    if (!data) return [];
    const results = data.results || [];
    const upcoming = data.upcoming || [];
    const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const keyOf = (x) => `${slug(x.rodeo_name)}||${slug(x.event)}`;
    // Group every payout line at the same rodeo+event into ONE card (e.g. Ponoka's
    // go-rounds + average), the way the CFR card lists its rounds.
    const groups = new Map();
    for (const r of results) {
      const k = keyOf(r);
      let g = groups.get(k);
      if (!g) {
        g = { kind: "result", rodeo_name: r.rodeo_name, event: r.event, date: r.date,
              lines: [], earnings: 0, sortDate: r.date || r.performance_date || "" };
        groups.set(k, g);
      }
      g.lines.push({ place: r.place, value: r.value, earnings: r.earnings || 0, is_average: r.is_average, stock: r.stock, stock_link: r.stock_link });
      g.earnings += r.earnings || 0;
    }
    for (const u of upcoming) {
      const g = groups.get(keyOf(u));
      if (g) { if (!g.role) { g.role = u.role; g.partner = u.partner; } (g.perfs = g.perfs || []).push(u); }
    }
    const rows = [...groups.values()];
    for (const g of rows) g.lines.sort((a, b) => ((a.is_average ? 1 : 0) - (b.is_average ? 1 : 0)) || ((b.earnings || 0) - (a.earnings || 0)));
    // Group a rider's drawn performances at the same rodeo+event into ONE card (e.g. Ponoka's
    // two go-rounds), so an entered multi-go rodeo isn't listed as several separate cards.
    const upGroups = new Map();
    for (const u of upcoming) {
      const k = keyOf(u);
      if (groups.has(k)) continue;
      let g = upGroups.get(k);
      if (!g) {
        g = { kind: "upcoming", rodeo_name: u.rodeo_name, event: u.event, role: u.role, partner: u.partner, perfs: [], sortDate: u.performance_date || u.date || "" };
        upGroups.set(k, g);
      }
      g.perfs.push(u);
      if (u.performance_date && u.performance_date > g.sortDate) g.sortDate = u.performance_date;
    }
    for (const g of upGroups.values()) rows.push(g);
    // One card per rodeo: "Ponoka" + "Ponoka Tie-Down Roping" + "Ponoka Team Roping" sit under the main rodeo.
    const byRodeo = new Map();
    for (const r of rows) {
      if (evFilter && canonRodeoEvent(r.event) !== evFilter) continue;
      const base = baseRodeoName(r.rodeo_name);
      const k = slug(base);
      let g = byRodeo.get(k);
      if (!g) { g = { rodeo_name: base, items: [], earnings: 0, sortDate: "" }; byRodeo.set(k, g); }
      g.items.push(r);
      g.earnings += r.earnings || 0;
      if ((r.sortDate || "") > g.sortDate) g.sortDate = r.sortDate || "";
    }
    const out = [...byRodeo.values()];
    out.sort((a, b) => (b.sortDate || "").localeCompare(a.sortDate || ""));
    return out;
  }, [data, evFilter]);

  return (
    <div className={`rounded-xl ${card}`} data-testid={`my-riders-card-${name}`}>
      <div className={`sticky top-0 z-20 flex items-center gap-2 px-4 py-3 ${m ? "bg-[#161B22]" : "bg-white"} ${open ? "rounded-t-xl border-b " + rowBorder : "rounded-xl"}`}>
        <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 flex-1 min-w-0 text-left" data-testid={`my-riders-expand-${name}`}>
          <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""} ${sub}`} />
          <span className={`font-display font-bold text-[16px] truncate ${title}`}>{name}</span>
          {isSelf && <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#C8000F] text-white">You</span>}
          {trending && (
            <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#E68A2E]/20 text-[#E68A2E]" data-testid={`my-riders-trending-${name}`} title="Trending — most-watched this week">
              <Flame className="w-3 h-3" /> Trending
            </span>
          )}
          {followers != null && (
            <span className={`shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${chip}`} data-testid={`my-riders-followers-${name}`} title={`${followers} ${followers === 1 ? "fan is" : "fans are"} following`}>
              <Users className="w-3 h-3" /> {followers}
            </span>
          )}
          {(() => {
            const combined = data ? (data.total_won || 0) + (data.cfr?.total_won || 0) : (preTotal?.total ?? null);
            return combined != null ? <span className={`shrink-0 text-[12px] font-semibold px-2 py-0.5 rounded-full ${chip}`} data-testid={`my-riders-total-${name}`}>{money(combined)}</span> : null;
          })()}
        </button>
        <button onClick={onMap} title="Show on map" data-testid={`my-riders-map-${name}`} className={`shrink-0 p-1.5 rounded-lg ${chip}`}><MapPin className="w-4 h-4" /></button>
        {onUnfollow && <button onClick={onUnfollow} title="Unfollow" data-testid={`my-riders-unfollow-${name}`} className="shrink-0 p-1.5 rounded-lg text-[#C8000F]"><Star className="w-4 h-4 fill-current" /></button>}
      </div>

      {open && (
        <div className={`rounded-b-xl px-4 py-3`}>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>
          ) : !data ? null : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Stat chip={chip} label="Rodeos" value={data.counts?.rodeos ?? 0} />
                <Stat chip={chip} label="Competed" value={data.counts?.competed ?? 0} />
                <Stat chip={chip} label="Placings" value={data.counts?.placings ?? 0} />
                <Stat chip={chip} label="Win %" value={`${data.counts?.win_pct ?? 0}%`} />
                <Stat
                  chip={chip}
                  label={data.cfr ? "Season + CFR $" : "Season $"}
                  value={money((data.total_won || 0) + (data.cfr?.total_won || 0))}
                  note={data.cfr ? `S ${money(data.total_won || 0)} · CFR ${money(data.cfr.total_won || 0)}` : null}
                />
              </div>

              {data.cfr && (
                <div className={`rounded-xl border p-3 ${m ? "border-[#C8000F]/40 bg-[#C8000F]/10" : "border-[#C8000F]/30 bg-[#FDF2F3]"}`} data-testid="rider-cfr-block">
                  <div className="flex items-center justify-between gap-2">
                    <div className={`font-display font-bold text-sm flex items-center gap-1.5 ${title}`}>
                      <Trophy className="w-4 h-4 text-[#C8000F]" /> Canadian Finals Rodeo
                    </div>
                    <span className="text-[11px] font-semibold" style={{ color: WON }}>{money(data.cfr.total_won || 0)}</span>
                  </div>
                  <div className={`text-[11px] mt-0.5 ${sub}`}>{data.cfr.event} · {data.cfr.runs} run{data.cfr.runs === 1 ? "" : "s"} · {data.cfr.placings} placing{data.cfr.placings === 1 ? "" : "s"}</div>
                  <div className="mt-2 space-y-1">
                    {data.cfr.rounds.map((rd) => {
                      const won = (rd.payout || 0) > 0;
                      return (
                        <div key={rd.round} className={`flex items-center justify-between gap-2 text-[12px] ${sub}`} data-testid={`rider-cfr-round-${rd.round}`}>
                          <span><b className={title}>Round {rd.round}</b>{rd.score_or_time ? ` · ${rd.score_or_time}` : ""}{rd.place ? ` · ${rd.place}` : ""}</span>
                          {won && <span className="font-semibold" style={{ color: WON }}>{money(rd.payout)}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {data.season?.by_event?.length > 0 && (
                <Section title="Season standings" icon={Trophy} sub={sub}>
                  <div className="space-y-1">
                    {data.season.by_event.map((e, i) => (
                      <div key={i} className={`flex items-center justify-between text-[13px] py-1 border-b last:border-0 ${rowBorder}`}>
                        <span className={title}>{e.event}{e.rank ? <span className={`ml-2 ${sub}`}>#{e.rank}</span> : null}</span>
                        <span className="font-semibold" style={{ color: WON }}>{money(e.won)}</span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {rodeoRows.length > 0 && (
                <Section title={`Rodeos (${rodeoRows.length})`} icon={CalendarClock} sub={sub}>
                  <div className="space-y-2">
                    {rodeoRows.map((g, gi) => (
                      <div key={gi} className={`rounded-lg border px-3 py-2 ${rowBorder}`} data-testid={`my-riders-rodeo-${name}-${gi}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className={`font-semibold text-[14px] truncate ${title}`}>{g.rodeo_name}</div>
                          {g.earnings > 0 && <span className="shrink-0 font-semibold text-[13px]" style={{ color: WON }}>{money(g.earnings)}</span>}
                        </div>
                        {g.items.map((r, i) => <EventLines key={i} r={r} m={m} nav={nav} title={title} sub={sub} rowBorder={rowBorder} testid={`my-riders-rodeo-row-${name}-${gi}-${i}`} />)}
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {!rodeoRows.length && (
                <p className={`text-[13px] ${sub}`}>{evFilter ? `No ${evFilter} entries or results for this rider yet.` : "No entries or results found for this rider yet."}</p>
              )}

              <RiderMedia name={name} m={m} title={title} sub={sub} isAdmin={isAdmin} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EntryLine({ p, sub, nav, m }) {
  return (
    <div className={`text-[12px] ${sub}`}>
      <span className="font-semibold">Entry:</span> {p.performance_label}{p.performance_date ? ` · ${p.performance_date}` : ""}{p.position ? ` · #${p.position} out` : ""}{p.partner ? ` · w/ ${p.partner}` : ""}
      {p.stock && <StockLink stock={p.stock} nav={nav} m={m} prefix="drew" />}
    </div>
  );
}

// One event under a rodeo card: its draw entry line(s) and its result line(s).
function EventLines({ r, m, nav, title, sub, rowBorder, testid }) {
  const lineLabel = (ln) => {
    const pnum = Number(ln.place);
    return Number.isFinite(pnum) && pnum > 0 ? `${ordinal(pnum)} place` : (ln.place ? String(ln.place) : "Competed");
  };
  return (
    <div className={`mt-1.5 pt-1.5 border-t ${rowBorder}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2 text-[13px]">
        <span className={`font-medium ${title}`}>{r.event}{r.role ? <span className={`font-normal ${sub}`}> ({r.role})</span> : null}</span>
        {r.kind === "result" && <span className="shrink-0 font-semibold" style={{ color: r.earnings > 0 ? WON : (m ? "#64748B" : "#94A3B8") }}>{r.earnings > 0 ? money(r.earnings) : "$0"}</span>}
      </div>
      {(r.perfs || []).map((p, pi) => <EntryLine key={pi} p={p} sub={sub} nav={nav} m={m} />)}
      {r.kind === "result" && (r.lines || []).map((ln, j) => (
        <div key={j} className={`flex items-start justify-between gap-2 text-[12px] ${sub}`} data-testid={`${testid}-line-${j}`}>
          <div className="min-w-0">
            <div><span className="font-semibold">Result:</span> {ln.is_average ? "Average" : lineLabel(ln)}{ln.value ? ` · ${ln.value}` : ""}{r.lines.length === 1 && r.date ? ` · ${r.date}` : ""}</div>
            {ln.stock && !ln.is_average && <StockLink stock={ln.stock} to={ln.stock_link} nav={nav} m={m} />}
          </div>
          {r.lines.length > 1 && ln.earnings > 0 && <span className="shrink-0 font-semibold" style={{ color: WON }}>{money(ln.earnings)}</span>}
        </div>
      ))}
    </div>
  );
}

const EVENT_SUFFIX = /\s*[-–:(]?\s*(tie[\s-]?down roping|tie[\s-]?down|team roping|breakaway roping|breakaway|steer roping|steer wrestling|barrel racing|bull riding|bareback riding|saddle bronc riding|slack)\s*\)?\s*$/i;
const baseRodeoName = (n) => { const s = (n || "").replace(EVENT_SUFFIX, "").trim(); return s || n || ""; };
const RIDER_EVENTS = ["Bareback Riding", "Saddle Bronc Riding", "Bull Riding", "Tie-Down Roping", "Steer Wrestling", "Team Roping", "Barrel Racing", "Breakaway Roping", "Steer Roping"];

function StockLink({ stock, to, nav, m, prefix }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); nav(`/stock?animal=${encodeURIComponent(to || stock)}`); }}
      data-testid="my-riders-stock-link"
      title="See everyone who's been on this animal"
      className={`mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium rounded-md px-1.5 py-0.5 max-w-full transition-colors ${m ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-[#F1F3F5] text-slate-600 hover:bg-[#E7EAEE]"}`}
    >
      <CowIcon className="w-3 h-3 shrink-0 text-[#C8000F]" />
      <span className="truncate">{prefix ? `${prefix} ` : ""}{stock}</span>
    </button>
  );
}

function Stat({ label, value, chip, note }) {
  return (
    <div className={`flex-1 min-w-[72px] rounded-lg px-3 py-2 text-center ${chip}`}>
      <div className="text-[11px] font-bold uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-[15px] font-extrabold mt-0.5">{value}</div>
      {note && <div className="text-[10px] font-semibold opacity-60 mt-0.5">{note}</div>}
    </div>
  );
}

function Section({ title, icon: Icon, sub, children }) {
  return (
    <div>
      <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide mb-1.5 ${sub}`}>
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      {children}
    </div>
  );
}
